import { SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Space, Table, Typography } from 'antd';
import nhsvStockRightsApiCall, {
  StockRight,
  StockRightEvent,
  StockRights,
} from 'apps/nhsv/apiCalls/nhsv/stock-rights';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const { Title, Text } = Typography;

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const m = moment(value, 'YYYY-MM-DD', true);
  return m.isValid() ? m.format('DD/MM/YYYY') : value;
};

const formatNumber = (value: number | undefined) =>
  value ? value.toLocaleString('vi-VN') : '-';

const date = (title: string, dataIndex: string) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: formatDate,
  width: 110,
});
const number = (title: string, dataIndex: string) => ({
  title,
  dataIndex,
  key: dataIndex,
  render: formatNumber,
  align: 'right' as const,
  width: 110,
});

const rightColumns = (dateTitle: string) => [
  { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 80 },
  { title: 'Kind', dataIndex: 'kind_label', key: 'kind_label', width: 180 },
  date('Record Date', 'record_date'),
  { title: 'Ratio', dataIndex: 'ratio', key: 'ratio', width: 90, render: (v: string) => v || '-' },
  number('Shares', 'quantity'),
  number('Cash (after tax)', 'amount'),
  { title: 'Tax %', dataIndex: 'tax_rate', key: 'tax_rate', width: 70, render: (v: number) => (v ? `${v}%` : '-') },
  date(dateTitle, 'date'),
];

const subscriptionColumns = [
  { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 80 },
  date('Record Date', 'record_date'),
  { title: 'Ratio', dataIndex: 'ratio', key: 'ratio', width: 90, render: (v: string) => v || '-' },
  number('Issue Price', 'price'),
  number('May Buy', 'quantity'),
  number('Registered', 'registered_quantity'),
  date('Register From', 'register_from'),
  date('Register To', 'register_to'),
];

const exRightsColumns = [
  { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 80 },
  { title: 'Becomes', dataIndex: 'kind_label', key: 'kind_label', width: 180 },
  { title: 'Event (Wifeed)', dataIndex: 'title', key: 'title' },
  date('Ex-rights Date', 'ex_date'),
  date('Record Date', 'record_date'),
];

const rightKey = (r: StockRight, i: number) => `${r.symbol}_${r.kind}_${r.record_date}_${i}`;
const eventKey = (e: StockRightEvent, i: number) => `${e.symbol}_${e.kind}_${e.ex_date}_${i}`;

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <div>
      <Title level={5} style={{ marginBottom: 4 }}>
        {title}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {note}
      </Text>
      {children}
    </div>
  );
}

// What an account's user sees on the app's "Quyền cổ đông" screen, holding
// card and "Sự kiện quyền" tag, from the same service. Raw NHSV lists are on
// the Rights History page.
export default function NHSVAppRightsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountNumber = searchParams.get('account_number') || '';
  const urlSymbol = searchParams.get('symbol') || '';

  const [accountNumber, setAccountNumber] = useState(urlAccountNumber);
  const [symbol, setSymbol] = useState(urlSymbol);

  const { data, load, isLoading } = useFetchApiGet(nhsvStockRightsApiCall, {
    resourceName: 'data',
  });

  const search = (account: string, sym: string) => {
    const trimmed = account.trim();
    if (!trimmed) return;
    const trimmedSymbol = sym.trim().toUpperCase();
    setAccountNumber(trimmed);
    setSymbol(trimmedSymbol);
    const params = trimmedSymbol
      ? { account_number: trimmed, symbol: trimmedSymbol }
      : { account_number: trimmed };
    setSearchParams(params);
    load(params);
  };

  useEffect(() => {
    if (urlAccountNumber) search(urlAccountNumber, urlSymbol);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rights = data as StockRights | undefined;
  const table = (rows: any[], columns: any[], rowKey: any, empty: string) => (
    <Table
      dataSource={rows}
      columns={columns}
      rowKey={rowKey}
      pagination={{ pageSize: 50, hideOnSinglePage: true }}
      size="small"
      scroll={{ x: 1000 }}
      locale={{ emptyText: empty }}
      loading={isLoading}
    />
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>App Rights View</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        The shareholder rights the user sees in the app (GET /portfolios/rights), from the same
        service: NHSV's lists (ASSET-011/012/013) split into pending, subscriptions and received,
        plus events of held stocks past their ex-rights date that NHSV has not listed yet.
      </Text>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap>
          <Input
            placeholder="Enter trading account number"
            size="large"
            value={accountNumber}
            onChange={e => setAccountNumber(e.target.value)}
            onPressEnter={() => search(accountNumber, symbol)}
            style={{ width: 300 }}
            allowClear
          />
          <Input
            placeholder="Symbol (optional)"
            size="large"
            value={symbol}
            onChange={e => setSymbol(e.target.value)}
            onPressEnter={() => search(accountNumber, symbol)}
            style={{ width: 180 }}
            allowClear
          />
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={() => search(accountNumber, symbol)}
            loading={isLoading}
          >
            Lookup
          </Button>
        </Space>

        {rights && !rights.available && (
          <Alert
            type="warning"
            showIcon
            message="This account cannot sync NHSV data, so the app shows no rights anywhere."
          />
        )}

        {rights?.available && (
          <>
            <Section
              title="Ex-rights, not listed by NHSV yet"
              note={
                'Shown in the app as the "Sự kiện quyền" tag, under Chờ về (Quyền mua for an offering). ' +
                'Dropped once NHSV lists a matching right, or 3 days after the record date.'
              }
            >
              {table(rights.ex_rights || [], exRightsColumns, eventKey, 'No event waiting for NHSV')}
            </Section>
            <Section title="Pending (Chờ về)" note="Shares not credited or cash not paid yet.">
              {table(rights.pending, rightColumns('Expected'), rightKey, 'Nothing pending')}
            </Section>
            <Section title="Subscriptions (Quyền mua)" note="Rights offerings whose registration has not ended.">
              {table(rights.subscriptions, subscriptionColumns, rightKey, 'No open offering')}
            </Section>
            <Section title="Received (Đã nhận)" note="Latest record date first.">
              {table(rights.received, rightColumns('Received'), rightKey, 'Nothing received')}
            </Section>
          </>
        )}
      </Space>
    </div>
  );
}
